import { useMemo } from "react";
import { MINUTE_MS, type Exercise, type TimedSessionResult } from "@heraion/shared";

import { useNowTick } from "../useNowTick";
import { buildMuscleCoverage } from "./muscleCoverage";
import type { ProgressDeps } from "./types";
import { useSessionResultsFromDeps } from "./useSessionResults";

type CoverageInputs = Pick<ProgressDeps, "weekStartDay" | "athleteTargets" | "trainingPlace">;

/** The coverage census from the results and a host's deps: the hook below,
 *  and #492 F5's on-demand build (usePlanGapsLauncher). */
export const coverageOf = (
  results: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  nowMs: number,
  inputs: CoverageInputs,
) =>
  buildMuscleCoverage(
    results,
    catalog,
    nowMs,
    inputs.weekStartDay,
    inputs.athleteTargets ?? null,
    inputs.trainingPlace,
  );

// The coverage census from a host's ProgressDeps: the SAME single-flight
// session results the panels read, scored the same way. Lifted out of
// ProgressView so the Create hub's «Build today's session» (AP-9b) reads the
// gaps the Progress card shows, and not a second computation of them.
export function useMuscleCoverage(deps: ProgressDeps) {
  const { results, catalog, error, status } = useSessionResultsFromDeps(deps);
  // The last 7 days slide: re-read the clock each minute.
  const nowMs = useNowTick(true, MINUTE_MS);
  const { weekStartDay, athleteTargets, trainingPlace } = deps;
  const coverage = useMemo(
    () =>
      results && catalog
        ? coverageOf(results, catalog, nowMs, { weekStartDay, athleteTargets, trainingPlace })
        : null,
    [results, catalog, nowMs, weekStartDay, athleteTargets, trainingPlace],
  );
  return { coverage, results, catalog, error, status };
}
